import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import EnquiryForm from "../components/EnquiryForm";
import { MapPin, EnvelopeSimple, Phone, Clock, ChatCircleDots } from "@phosphor-icons/react";
import { GettingHere } from "../components/GettingHere";
import { CampusMap } from "../components/CampusMap";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact & Enquiries"
        description="Speak to the Cambridge Cardiovascular Prevention Clinic team. Use our enquiry form for general questions or anything you’d like to discuss before booking online."
        path="/contact"
      />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 max-w-4xl">
          <div className="eyebrow">Contact & enquiries</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">We’re here to help.</h1>
          <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-2xl">
            Got a question? Please note we only see self-pay patients. Drop us a line and we will be back with you shortly. If something feels urgent, please contact your GP or call 111. In an emergency, always call 999.
          </p>
        </div>
      </section>

      <section className="container-ccc py-16 grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-7">
          <div className="eyebrow">Send us a message</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-2 leading-tight">Tell us what you need.</h2>
          <p className="mt-3 text-ink-muted text-sm max-w-xl">
            Use this form for general questions, rescheduling, or anything you’d like to discuss before you book.{" "}
            <Link to="/enquiry" className="text-bronze-dark underline">Want to book a paid online slot instead? Click here →</Link>
          </p>
          <div className="mt-8">
            <EnquiryForm />
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="p-7 bg-white border border-stone-200/60 rounded-md">
            <div className="eyebrow">Clinic address</div>
            <div className="mt-4 flex gap-3 items-start">
              <MapPin size={22} weight="duotone" className="text-bronze-dark mt-0.5" />
              <div className="text-ink text-sm leading-relaxed">
                ACCI Building<br />
                Cambridge University Hospitals<br />
                Hills Road, Cambridge<br />
                CB2 0QQ
              </div>
            </div>
          </div>

          <GettingHere />
          <CampusMap />

          <div className="p-7 bg-white border border-stone-200/60 rounded-md">
            <div className="eyebrow">Reach the team directly</div>
            <ul className="mt-4 space-y-4 text-sm text-ink">
              <li className="flex gap-3 items-center"><Phone size={20} weight="duotone" className="text-bronze-dark shrink-0" /><a href="tel:+441223256653" className="hover:text-bronze-dark">+44 (0) 1223 256653</a></li>
              <li className="flex gap-3 items-center"><EnvelopeSimple size={20} weight="duotone" className="text-bronze-dark shrink-0" /><a href="mailto:enquiries@camcpc.co.uk" className="hover:text-bronze-dark break-all">enquiries@camcpc.co.uk</a></li>
              <li className="flex gap-3 items-center"><Clock size={20} weight="duotone" className="text-bronze-dark shrink-0" /> Mon–Fri · 08:30 – 18:00</li>
            </ul>
          </div>

          <div className="p-7 bg-cream-100 border border-bronze-light/40 rounded-md">
            <div className="flex items-center gap-2"><ChatCircleDots size={18} weight="duotone" className="text-bronze-dark" /><div className="eyebrow">Just want to book?</div></div>
            <p className="mt-3 text-sm text-ink-muted">If you’re a self-pay patient, you can book and pay online by card.</p>
            <Link data-testid="contact-book-cta" to="/enquiry" className="btn-primary mt-5 w-full">Book online</Link>
          </div>
        </aside>
      </section>

      <section className="container-ccc pb-20">
        <div className="aspect-[16/7] overflow-hidden rounded-md border border-bronze-light/40">
          <iframe
            title="Clinic map"
            src="https://www.openstreetmap.org/export/embed.html?bbox=0.129,52.174,0.151,52.180&layer=mapnik&marker=52.177,0.1402"
            className="w-full h-full border-0"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
